// import components
import { Box } from '@mui/material';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import external libraries
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

// import types
import dayjs from 'dayjs';
import type { TreatmentResponse } from 'generated/index';
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid';

interface PatientTreatmentsProps {
  treatments: TreatmentResponse[];
  isLoading?: boolean;
}

const formatDate = (date?: string) => {
  if (!date) return '';
  return dayjs(date).format('DD-MM-YYYY');
};

const PatientTreatments = ({
  treatments,
  isLoading
}: PatientTreatmentsProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'careReceiversHome' });

  const rows = useMemo(
    () =>
      treatments.map((treatment, index) => ({
        ...treatment,
        id: `treatment-${index}`
      })),
    [treatments]
  );

  const columns = useMemo<GridColDef<GridValidRowModel>[]>(
    () => [
      {
        headerName: t('treatments.programName'),
        field: 'programName',
        type: 'string',
        flex: 1.5,
        minWidth: 200
      },
      {
        headerName: t('treatments.groupName'),
        field: 'groupName',
        type: 'string',
        flex: 1,
        minWidth: 150
      },
      {
        headerName: t('treatments.departmentName'),
        field: 'departmentName',
        type: 'string',
        flex: 1,
        minWidth: 150
      },
      {
        // Next to the department it was derived from: the plan unit is the result of matching the
        // ECD's department id against the plan unit's code, so the two read together. Empty when no
        // plan unit carries that department id as its code - normal rather than exceptional.
        headerName: t('treatments.organizationalUnitName'),
        field: 'organizationalUnitName',
        type: 'string',
        flex: 1,
        minWidth: 150
      },
      {
        headerName: t('treatments.startDate'),
        field: 'actualOrPlannedOrExpectedStartDate',
        type: 'date',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => formatDate(value)
      },
      {
        headerName: t('treatments.endDate'),
        field: 'actualOrPlannedOrExpectedEndDate',
        type: 'date',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => formatDate(value)
      }
    ],
    [t]
  );

  return (
    <Box p={3.5}>
      <TableCRUDLayout
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        loading={isLoading}
        dynamicHeight={true}
        pageSize={25}
        editable={false}
        disableActions={true}
        hideFooter={rows.length < 25}
        initialState={{
          sorting: {
            sortModel: [
              { field: 'actualOrPlannedOrExpectedStartDate', sort: 'desc' }
            ]
          }
        }}
      />
    </Box>
  );
};

export default PatientTreatments;
